import { StandardEvents } from '@shopify/events';

/* Carousel arrows for zb product rails */
class ZbRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-zb-track]');
    this.prev = this.querySelector('[data-zb-prev]');
    this.next = this.querySelector('[data-zb-next]');
    if (!this.track) return;
    const step = () => {
      const card = this.track.querySelector(':scope > *');
      return card ? card.getBoundingClientRect().width + 12 : this.track.clientWidth * 0.8;
    };
    this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
    this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
    const update = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    };
    this.track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }
}
if (!customElements.get('zb-rail')) customElements.define('zb-rail', ZbRail);

/* Re-renders server-side PDP blocks (stock bar, SKU line) when the variant changes */
class ZbVariantSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.onSelect);
  }
  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }
  onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        const fresh = detail.html.querySelector(`zb-variant-sync[data-key="${this.dataset.key}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}
if (!customElements.get('zb-variant-sync')) customElements.define('zb-variant-sync', ZbVariantSync);

/* Size guide modal */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-zb-open]');
  if (opener) {
    const dlg = document.getElementById(opener.dataset.zbOpen);
    if (dlg?.showModal) {
      e.preventDefault();
      dlg.showModal();
    }
    return;
  }
  const closer = e.target.closest('[data-zb-close]');
  if (closer) closer.closest('dialog')?.close();
  if (e.target instanceof HTMLDialogElement && e.target.classList.contains('zb-modal')) e.target.close();
});

/* Banner slideshow: fade, autoplay, dots, arrows, swipe, pause on hover / hidden tab */
class ZbSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.zb-ss__slide')];
    this.dots = [...this.querySelectorAll('[data-zb-ss-dot]')];
    this.index = 0;
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.style.setProperty('--zb-ss-interval', `${this.interval}ms`);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.auto = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reduced;
    if (this.slides.length < 2) return;

    this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.zbSsDot, 10), true)));
    this.querySelector('[data-zb-ss-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-zb-ss-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));

    let x0 = null;
    this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
      x0 = null;
    }, { passive: true });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.go(this.index + 1, true);
      if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
    });

    // Theme editor: jump to the selected slide
    document.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
      if (i > -1) { this.go(i); this.pause(true); }
    });
    document.addEventListener('shopify:block:deselect', () => this.play(true));

    this.play();
  }

  go(i, user = false) {
    const n = this.slides.length;
    const next = ((i % n) + n) % n;
    if (next === this.index) return;
    const prev = this.slides[this.index];
    const cur = this.slides[next];
    prev.classList.remove('is-active');
    prev.setAttribute('aria-hidden', 'true');
    cur.classList.add('is-active');
    cur.removeAttribute('aria-hidden');
    cur.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
    this.dots.forEach((d, k) => {
      d.classList.toggle('is-active', k === next);
      d.setAttribute('aria-selected', k === next ? 'true' : 'false');
    });
    this.index = next;
    // preload the following slide
    this.slides[(next + 1) % n].querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
    if (user) this.restart();
  }

  play(force = false) {
    if (!this.auto) return;
    if (this.editorHold && !force) return;
    if (force) this.editorHold = false;
    this.removeAttribute('data-paused');
    clearInterval(this.timer);
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  pause(editor = false) {
    if (editor) this.editorHold = true;
    this.setAttribute('data-paused', '');
    clearInterval(this.timer);
  }

  restart() {
    if (!this.auto || this.hasAttribute('data-paused')) return;
    this.play();
    // restart the dot progress animation
    const bar = this.querySelector('.zb-ss__dot.is-active span');
    if (bar) { bar.style.display = 'none'; void bar.offsetWidth; bar.style.display = ''; }
  }

  disconnectedCallback() { clearInterval(this.timer); }
}
if (!customElements.get('zb-slideshow')) customElements.define('zb-slideshow', ZbSlideshow);
